/**
 * Jun-2026 (· 261, User-Wunsch) — Dezenter, einmalig schließbarer Banner, der
 * Händler & Mitarbeiter beim (ersten) Login einlädt, Push-Benachrichtigungen
 * für Aufgaben-Erinnerungen zu aktivieren. Beim Klick wird ein echtes
 * Push-Abo angelegt (sonst erreichen die Überfällig-Alerts den Mitarbeiter nicht).
 *
 * Sichtbar (alle Bedingungen müssen erfüllt sein):
 *   1. Browser unterstützt Notifications/Push
 *   2. Noch keine Entscheidung getroffen (`permission === "default"`)
 *   3. Rolle b2b oder zweiradverkaeufer (Aufgaben-relevant)
 *   4. Noch nicht abonniert
 *   5. Banner wurde in diesem Browser noch nicht weggeklickt
 */
import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { getPushStatus, registerPushSubscription } from "../lib/webPush";

const DISMISS_KEY = "tw_task_push_prompt_dismissed";
const TASK_ROLES = ["b2b", "zweiradverkaeufer"];

export default function TaskPushPrompt() {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    const eligible = user && user !== false && TASK_ROLES.includes(user.role);

    useEffect(() => {
        if (!eligible) { setVisible(false); return undefined; }
        let cancelled = false;
        (async () => {
            try {
                if (localStorage.getItem(DISMISS_KEY) === "1") return;
            } catch { /* noop */ }
            try {
                const st = await getPushStatus();
                if (cancelled) return;
                if (st.supported && st.permission === "default" && !st.subscribed) {
                    setVisible(true);
                }
            } catch { /* unsupported */ }
        })();
        return () => { cancelled = true; };
    }, [eligible]);

    const enable = async () => {
        setBusy(true);
        try {
            const res = await registerPushSubscription();
            if (res?.ok || res?.subscribed) {
                toast.success("Push-Benachrichtigungen aktiviert — du wirst bei überfälligen Aufgaben erinnert.");
                setVisible(false);
            } else if (res?.permission === "denied") {
                dismiss();
            } else {
                setVisible(false);
            }
        } catch {
            toast.error("Aktivierung fehlgeschlagen. Bitte Browser-Berechtigung prüfen.");
        } finally {
            setBusy(false);
        }
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            className="fixed z-40 bottom-16 md:bottom-4 left-2 right-2 md:left-auto md:right-4 md:max-w-sm bg-[#111827] text-white rounded-sm shadow-xl border-l-4 border-[#FFCC00] px-3 py-2.5 flex items-center gap-2"
            role="region"
            aria-label="Push-Benachrichtigungen aktivieren"
            data-testid="task-push-prompt"
        >
            <span className="w-9 h-9 rounded-sm bg-[#FFCC00] text-[#111827] flex items-center justify-center shrink-0">
                <Bell className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0 leading-tight">
                <div className="font-bold text-xs uppercase tracking-wider">Aufgaben-Erinnerungen</div>
                <div className="text-[11px] text-white/70">Push aktivieren, um bei überfälligen Aufgaben benachrichtigt zu werden.</div>
            </div>
            <button
                type="button"
                onClick={enable}
                disabled={busy}
                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm shrink-0 disabled:opacity-60"
                data-testid="task-push-allow"
            >
                {busy ? "…" : "Aktivieren"}
            </button>
            <button
                type="button"
                onClick={dismiss}
                className="text-white/60 hover:text-white shrink-0"
                aria-label="Hinweis schließen"
                data-testid="task-push-dismiss"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
